import axios, { AxiosError } from "axios";
import { showErrorToast, showSuccessToast } from "@/components/ui/toast";

// URL de base de l'API (utilise VITE_API_URL en prod, localhost en dev)
const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.pointo.fr";
// console.log("API_BASE_URL", API_BASE_URL);
// console.log("VITE_API_URL", import.meta.env.VITE_API_URL);

// Instance Axios configurée
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 secondes de timeout
});

// ========================================
// INTERCEPTEUR DE REQUÊTE (ajoute le token JWT)
// ========================================
api.interceptors.request.use(
  (config) => {
    // Récupérer le token depuis localStorage
    const token = localStorage.getItem("token");

    // Si un token existe, l'ajouter au header Authorization
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ========================================
// INTERCEPTEUR DE RÉPONSE (gère les erreurs d'auth et réseau)
// ========================================
api.interceptors.response.use(
  (response) => {
    // Si la réponse est OK, la retourner
    return response;
  },
  (error: AxiosError) => {
    // 1. Pas de connexion internet ou serveur inaccessible
    if (!error.response && error.request) {
      // La requête a été envoyée mais pas de réponse reçue
      if (!navigator.onLine) {
        showErrorToast("Pas de connexion internet", {
          description: "Vérifiez votre connexion réseau et réessayez.",
        });
      } else if (
        error.code === "ECONNABORTED" ||
        error.message.includes("timeout")
      ) {
        // Timeout (requête trop longue)
        showErrorToast("La requête a pris trop de temps", {
          description:
            "Le serveur met trop de temps à répondre. Veuillez réessayer.",
        });
      } else {
        // Serveur inaccessible
        showErrorToast("Le serveur ne répond pas", {
          description: "Vérifiez que le serveur est démarré et réessayez.",
        });
      }
      return Promise.reject(error);
    }

    // 2. Erreur 401 (token expiré/invalide)
    if (error.response?.status === 401) {

      const isLoginRequest = error.config?.url === "/auth/login";

      if (!isLoginRequest) {
        // Supprimer le token invalide
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // Rediriger vers la page de login
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    // 3. Erreur 403 (pas les permissions)
    if (error.response?.status === 403) {
      showErrorToast("Accès refusé", {
        description:
          "Vous n'avez pas les permissions pour effectuer cette action.",
      });
      return Promise.reject(error);
    }

    // 4. Erreur 404 (ressource non trouvée)
    if (error.response?.status === 404) {
      showErrorToast("Ressource non trouvée", {
        description: "La ressource demandée n'existe pas ou a été supprimée.",
      });
      return Promise.reject(error);
    }

    // 5. Erreur 500 (erreur serveur)
    if (error.response?.status === 500) {
      showErrorToast("Erreur serveur", {
        description:
          "Une erreur interne s'est produite. Veuillez réessayer plus tard.",
      });
      return Promise.reject(error);
    }

    // 6. Erreur 503 (service indisponible / maintenance)
    if (error.response?.status === 503) {
      showErrorToast("Service indisponible", {
        description:
          "Le serveur est en maintenance. Veuillez réessayer plus tard.",
      });
      return Promise.reject(error);
    }

    // 7. Erreur 429 (trop de requêtes)
    if (error.response?.status === 429) {
      showErrorToast("Trop de requêtes", {
        description:
          "Vous avez effectué trop de requêtes. Veuillez patienter quelques instants.",
      });
      return Promise.reject(error);
    }

    // 8. Erreur 400 (mauvaise requête) - on laisse les composants gérer le message spécifique
    if (error.response?.status === 400) {
      return Promise.reject(error);
    }

    // 9. Erreur 409 (conflit) - géré par le service appelant (ex: shifts en doublon)
    if (error.response?.status === 409) {
      return Promise.reject(error);
    }

    // 10. Autres erreurs HTTP
    if (error.response?.status) {
      const statusCode = error.response.status;
      const errorData = error.response.data as { message?: string } | undefined;
      const message =
        errorData?.message || `Erreur ${statusCode}: Une erreur est survenue.`;
      showErrorToast(message);
      return Promise.reject(error);
    }

    // 11. Erreur inconnue
    showErrorToast("Une erreur est survenue", {
      description:
        "Veuillez réessayer ou contacter le support si le problème persiste.",
    });

    return Promise.reject(error);
  }
);

// ========================================
// FONCTIONS UTILITAIRES
// ========================================

/**
 * Sauvegarder le token JWT dans localStorage
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem("token", token);
};

/**
 * Récupérer le token JWT depuis localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem("token");
};

/**
 * Supprimer le token JWT (logout)
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
};

/**
 * Vérifier si l'utilisateur est authentifié
 */
export const isAuthenticated = (): boolean => {
  return getAuthToken() !== null;
};

// ========================================
// DÉTECTION DES CHANGEMENTS DE CONNEXION
// ========================================

/**
 * Écouter les changements de connexion réseau
 * Affiche une notification quand la connexion est perdue ou retrouvée
 */
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    showSuccessToast("Connexion internet rétablie", {
      description: "Vous pouvez continuer à utiliser l'application.",
    });
  });

  window.addEventListener("offline", () => {
    showErrorToast("Connexion internet perdue", {
      description: "Vérifiez votre connexion réseau.",
    });
  });
}
